/* details-disclosure.js
 *
 * Jul 2026 rewrite. The previous version shipped three methods whose bodies were
 * entirely commented out (`onFocusOut`, `onToggle` on the base class, and `close`).
 * `close()` in particular was a truthy no-op, so StickyHeader.closeMenuDisclosure()'s
 * `disclosure.close && disclosure.close()` guard passed and then closed nothing, and
 * keyboard users could never leave an open mega menu by tabbing.
 *
 * It also set --header-bottom-position-desktop exactly once and then returned early
 * forever, so the value froze at whatever the header's bottom happened to be on the
 * first open. The tracker below keeps it accurate, and only listens while the pointer
 * is in the menu or a panel is open, so there is no always-on scroll handler.
 *
 * What still reads the variable, as of the Jul 2026 nav rework (Task #46 / #50): one
 * rule, component-mega-menu.css line 21 --
 *
 *     .shopify-section-header-sticky .mega-menu__content {
 *       max-height: calc(100vh - var(--header-bottom-position-desktop, 20rem) - 4rem);
 *     }
 *
 * There used to be a second reader: custom.css pinned the 990-1440px mega-menu panel
 * with `position: fixed` and used this variable as its `top`. That whole workaround is
 * gone - the panel is back to `position: absolute; top: 100%`, which needs no measured
 * value at all. Only the max-height reader is left, and it subtracts the variable from
 * 100vh, so the number written here must be in VIEWPORT coordinates. See write().
 */

const HeaderBottomTracker = (() => {
  let headerEl = null;
  let rafId = 0;
  let listening = false;
  let pointerInMenu = false;

  function header() {
    if (!headerEl || !headerEl.isConnected) headerEl = document.querySelector('.header-wrapper');
    return headerEl;
  }

  /* The surviving consumer subtracts this value from 100vh, so it has to be a viewport
     -space quantity: how far down the screen the header ends. getBoundingClientRect()
     is already in that space, transforms included, so it is used unmodified.

     An earlier revision subtracted a containingBlockTop() offset here - the top of the
     nearest transform/filter/contain ancestor. That was right for the reader that has
     since been deleted (a `position: fixed` panel, whose `top` resolves against that
     ancestor rather than the viewport) and it is wrong for the one that is left, which
     never leaves viewport space. It is removed rather than kept as a harmless-looking
     no-op: it returned non-zero in exactly one state, .shopify-section-header-hidden,
     where #shopify-section-header carries translate3d(0,-100%,0) - and there it would
     now inflate max-height by a whole header's height.

     Clamped at 0 because a header translated off the top of the screen has a negative
     bottom, and a negative would make max-height taller than the viewport. */
  function write() {
    rafId = 0;
    const h = header();
    if (!h) return;
    const next = `${Math.max(0, Math.round(h.getBoundingClientRect().bottom))}px`;
    const root = document.documentElement;
    if (root.style.getPropertyValue('--header-bottom-position-desktop') !== next) {
      root.style.setProperty('--header-bottom-position-desktop', next);
    }
  }

  function schedule() {
    if (rafId) return;
    rafId = requestAnimationFrame(write);
  }

  function anyOpen() {
    return !!document.querySelector('header-menu details[open]');
  }

  function listen(on) {
    if (on === listening) return;
    listening = on;
    if (on) {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
    } else {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    }
  }

  function sync() {
    listen(pointerInMenu || anyOpen());
  }

  /* Bind once, to the desktop menu only. pointerenter/focusin fire on the <nav>
     before they fire on the <summary> inside it, so the variable is already correct
     by the time the hover script opens a panel. */
  function bindMenu() {
    const nav = document.querySelector('.header__inline-menu');
    if (!nav || nav.dataset.headerBottomBound === '1') return;
    nav.dataset.headerBottomBound = '1';
    nav.addEventListener('pointerenter', () => { pointerInMenu = true; write(); sync(); }, { passive: true });
    nav.addEventListener('pointerleave', () => { pointerInMenu = false; sync(); }, { passive: true });
    nav.addEventListener('focusin', () => { write(); sync(); });
    nav.addEventListener('focusout', () => { sync(); });
  }

  return { write, schedule, sync, bindMenu, anyOpen };
})();

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    /* focusout fires before the new element receives focus, so the check has to be
       deferred by one task or document.activeElement is still the old node. */
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {}

  close() {
    if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) return;
    this.mainDetailsToggle.removeAttribute('open');
    /* Mirror the desktop hover script, which pairs [open] with .mega-menu-active on the
       parent <li> (custom.css.liquid raises that li's z-index). Native <details>/<summary>
       already exposes the expanded state, and this theme's summaries carry no
       aria-expanded attribute, so none is written here. */
    const li = this.closest('li');
    if (li) li.classList.remove('mega-menu-active');
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    HeaderBottomTracker.bindMenu();
    HeaderBottomTracker.schedule();
  }

  onToggle() {
    if (!this.header) return;
    /* Read the live DOM instead of trusting this instance's own state: when the pointer
       moves from one mega menu to the next, two toggle events fire (close, then open)
       and a per-instance flag would leave preventHide wrong if they ever landed out of
       order. */
    this.header.preventHide = HeaderBottomTracker.anyOpen();
    if (this.mainDetailsToggle.hasAttribute('open')) HeaderBottomTracker.write();
    HeaderBottomTracker.sync();
  }
}

customElements.define('header-menu', HeaderMenu);
